import { lazy, Suspense } from "react";
import { EmptyState, Pagination } from "@/shared/components";
import { Prompt, PromptForm } from "@/shared/ui";
import { usePurchasesListPage, usePurchasesModals, usePurchasesMutations } from "../../hooks";
import { transactionStatusToPurchaseStatus } from "../../types/api";
import {
  PurchasesHeader,
  PurchasesFilter,
  PurchasesPageSkeleton,
} from "./components";

const PurchasesTable = lazy(() => import("./components/PurchasesTable").then((m) => ({ default: m.default })));
const PurchaseCreateModal = lazy(() => import("./components/PurchaseCreateModal").then((m) => ({ default: m.default })));
const PurchaseInfoModal = lazy(() => import("./components/PurchaseInfoModal").then((m) => ({ default: m.default })));
const PurchaseDetailsModal = lazy(() => import("../PurchaseDetailsModal").then((m) => ({ default: m.default })));
const OverrideReasonModal = lazy(() => import("@/features/accounting/smeta/components/OverrideReasonModal").then((m) => ({ default: m.default })));

export default function PurchasesPage() {
  const {
    data,
    isLoading,
    isError,
    filteredPurchases,
    pagination,
    pagePurchases,
    sortConfig,
    handleSort,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    handleResetFilters,
    locale,
    t,
  } = usePurchasesListPage();

  const {
    prompt,
    setPrompt,
    createModal,
    setCreateModal,
    editModalId,
    setEditModalId,
    detailModal,
    setDetailModal,
    quickViewModalId,
    setQuickViewModalId,
    confirmDelete,
    setConfirmDelete,
    confirmApprove,
    setConfirmApprove,
    confirmMarkPaid,
    setConfirmMarkPaid,
  } = usePurchasesModals();

  const {
    approvingId,
    markingPaidId,
    deletingId,
    isApproving,
    isMarkingPaid,
    isDeleting,
    creating,
    updating,
    extractingOCR,
    handleCreate,
    handleUpdate,
    handleApprove,
    handleMarkPaid,
    handleDelete,
    handleExtractOCR,
    overrideV2Pending,
    handleOverrideV2Confirm,
    closeOverrideV2,
  } = usePurchasesMutations(setPrompt);


  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <PurchasesHeader />
          <PurchasesPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <PurchasesHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  function findPurchaseByInternalId(internalId: string) {
    return data?.find((p) => p.internal_id === internalId);
  }

  async function handleDeleteConfirm(internalId: string) {
    const purchase = findPurchaseByInternalId(internalId);
    if (!purchase) {
      setConfirmDelete(null);
      return;
    }

    await handleDelete(purchase.id);
    setConfirmDelete(null);
  }

  function handleApproveClick(internalId: string) {
    const purchase = findPurchaseByInternalId(internalId);
    if (!purchase) return;
    const status = transactionStatusToPurchaseStatus(purchase.transaction_status);
    if (status !== "draft") return;

    setConfirmApprove(internalId);
  }

  async function handleApproveConfirm(internalId: string) {
    const purchase = findPurchaseByInternalId(internalId);
    if (!purchase) {
      setConfirmApprove(null);
      return;
    }

    const status = transactionStatusToPurchaseStatus(purchase.transaction_status);
    if (status === "draft") {
      await handleApprove(purchase.id);
    }

    setConfirmApprove(null);
  }

  async function handleMarkPaidConfirm(internalId: string) {
    const purchase = findPurchaseByInternalId(internalId);
    if (!purchase) {
      setConfirmMarkPaid(null);
      return;
    }

    const status = transactionStatusToPurchaseStatus(purchase.transaction_status);
    if (status === "approved") {
      await handleMarkPaid(purchase.id);
    }

    setConfirmMarkPaid(null);
  }

  const approvingKey = approvingId
    ? data?.find((p) => p.id === approvingId)?.internal_id
    : null;
  const markingPaidKey = markingPaidId
    ? data?.find((p) => p.id === markingPaidId)?.internal_id
    : null;
  const deletingKey = deletingId
    ? data?.find((p) => p.id === deletingId)?.internal_id
    : null;
  const editingKey = editModalId
    ? data?.find((p) => p.id === editModalId)?.internal_id
    : null;

  function handleEdit(internalId: string) {
    const purchase = findPurchaseByInternalId(internalId);
    if (purchase) {
      const status = transactionStatusToPurchaseStatus(purchase.transaction_status);
      if (status === "draft") {
        setEditModalId(purchase.id);
      }
    }
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <PurchasesHeader />

        <div className="radius-3xl surface-base-fill p-7">
          <PurchasesFilter
            search={search}
            onSearchChange={setSearch}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            statusOptions={statusOptions}
            locale={locale}
            onCreateClick={() => setCreateModal(true)}
            hasActiveFilters={activeFilters}
            onReset={handleResetFilters}
          />

          {filteredPurchases.length === 0 ? (
            <EmptyState
              hasActiveFilters={activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              <Suspense fallback={<PurchasesPageSkeleton variant="tableOnly" />}>
                <PurchasesTable
                  purchases={pagePurchases}
                  onViewQuick={(internalId) => {
                    const purchase = findPurchaseByInternalId(internalId);
                    if (purchase) {
                      setQuickViewModalId(purchase.id);
                    }
                  }}
                  onEdit={handleEdit}
                  onApprove={handleApproveClick}
                  onMarkPaid={(internalId) => setConfirmMarkPaid(internalId)}
                  onDelete={(internalId) => setConfirmDelete(internalId)}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  approvingKey={approvingKey}
                  markingPaidKey={markingPaidKey}
                  deletingKey={deletingKey}
                  editingKey={editingKey}
                />
              </Suspense>

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={filteredPurchases.length}
              />
            </>
          )}
        </div>
      </section>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="PurchasesPage"
        />
      )}

      {confirmDelete !== null && (
        <PromptForm
          title={t("confirm.deleteTitle")}
          text={t("confirm.deleteText")}
          variant="error"
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => handleDeleteConfirm(confirmDelete)}
          isLoading={isDeleting}
          confirmText={t("confirm.deleteConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="PurchasesPage"
        />
      )}

      {confirmApprove !== null && (
        <PromptForm
          title={t("confirm.approveTitle")}
          text={t("confirm.approveText")}
          variant="success"
          onClose={() => setConfirmApprove(null)}
          onConfirm={() => handleApproveConfirm(confirmApprove)}
          isLoading={isApproving}
          confirmText={t("confirm.approveConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="PurchasesPage"
        />
      )}

      {confirmMarkPaid !== null && (
        <PromptForm
          title={t("confirm.markPaidTitle")}
          text={t("confirm.markPaidText")}
          variant="success"
          onClose={() => setConfirmMarkPaid(null)}
          onConfirm={() => handleMarkPaidConfirm(confirmMarkPaid)}
          isLoading={isMarkingPaid}
          confirmText={t("confirm.markPaidConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="PurchasesPage"
        />
      )}

      {(createModal || editModalId !== null) && (
        <Suspense fallback={null}>
          <PurchaseCreateModal
            isOpen={createModal || editModalId !== null}
            onClose={() => {
              setCreateModal(false);
              setEditModalId(null);
            }}
            onConfirm={handleCreate}
            onUpdate={handleUpdate}
            onExtractOCR={handleExtractOCR}
            isLoading={creating || updating}
            isExtractingOCR={extractingOCR}
            editMode={editModalId !== null}
            purchaseId={editModalId}
          />
        </Suspense>
      )}

      {detailModal !== null && (
        <Suspense fallback={null}>
          <PurchaseDetailsModal purchaseId={detailModal} onClose={() => setDetailModal(null)} />
        </Suspense>
      )}

      {quickViewModalId !== null && (
        <Suspense fallback={null}>
          <PurchaseInfoModal purchaseId={quickViewModalId} onClose={() => setQuickViewModalId(null)} />
        </Suspense>
      )}

      {overrideV2Pending && (
        <Suspense fallback={null}>
          <OverrideReasonModal
            errorData={overrideV2Pending.errorData}
            onClose={closeOverrideV2}
            onConfirm={handleOverrideV2Confirm}
            isLoading={isApproving}
          />
        </Suspense>
      )}
    </>
  );
}
